/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Divider';

const DividerManager: ComponentManagerFn<'Divider'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Divider'>(platform, 'Divider'),
    type: 'Divider',
    name: 'Divider',
    defaultValue,
    props: {
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: toOptions(['vertical', 'horizontal']),
      },
      margin: {
        label: 'Margin',
        type: 'margin',
      },
      dividerWidth: {
        label: 'Width',
        type: 'string',
      },
      dividerHeight: {
        label: 'Height',
        type: 'string',
      },
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'dividerWidth':
          return item.orientation === 'horizontal';
        case 'dividerHeight':
          return item.orientation === 'vertical';
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.orientation,
    category: 'Dashboard',
    noOverlay: true,
    icon: { iconName: 'grip-lines' },
    description: `**Overview**<br/>
The Divider is a lightweight visual component used to create clear boundaries between UI elements. As interfaces grow dense with information, sections can begin to blend together, making them difficult to scan. The Divider solves this by inserting a crisp line that improves readability and establishes a visual hierarchy without adding clutter.

**1. Orientation & Direction**<br/>
The Divider creates separation in two distinct directions.
* **Orientation:** Controls the axis of the line.
* **Horizontal:** A line running from left to right. Use this to separate vertically stacked content (e.g., between a header and a body, or between form sections).
* **Vertical:** A line running from top to bottom. Use this to separate side-by-side elements (e.g., between columns in a panel or buttons in a toolbar).

**2. Sizing Constraints**<br/>
To keep the configuration intuitive, the Divider uses Smart Property Visibility. It dynamically shows only the size property relevant to the current orientation.
* **Width:** Sets the length of the line when the orientation is Horizontal. (Examples: 100% for full width, 50%, 300px).
* **Height:** Sets the length of the line when the orientation is Vertical. (Examples: 100% for full height of parent, 50px).
* **Note:** You cannot set the "thickness" of the line; standard styling is applied automatically to maintain consistency.

**3. Spacing (Breathing Room)**<br/>
Use spacing to prevent the divider from looking cramped or touching adjacent content.
* **Margin:** Adds invisible space around the divider. (Tip: Adding top/bottom margin to a horizontal divider effectively increases the visual gap between sections, while the line itself remains thin).

**Usage Guidelines**<br/>
* **Silent Helper:** The Divider is purely presentational. It does not accept child components, does not emit data, and does not support interaction (click/hover) events.
* **Layout Adaptability:** It respects the layout rules of its parent container (e.g., Flexbox or Grid) but does not enforce any layout logic of its own.`,
  };
};

export default DividerManager;
